/**
 * Item Categories Store
 *
 * Loads and caches the workspace's configurable item categories.
 * Used by CandidateTypeSelector, CreateItemModal, CreateFrameModal,
 * TrackIndex, and the ItemCategories settings page.
 */

import { create } from 'zustand';
import {
  listItemCategories,
  setCategoryActive,
  createCategory,
  updateCategory,
  deleteCategory,
  listCategoryQuestions,
  upsertCategoryQuestion,
  type WorkspaceItemCategory,
  type WorkspaceCategoryQuestion,
} from '../../lib/itemCategoriesService';

interface ItemCategoriesState {
  categories: WorkspaceItemCategory[];
  questions: Record<string, WorkspaceCategoryQuestion[]>; // keyed by categoryId
  loading: boolean;
  error: string | null;
  loadedWorkspaceId: string | null;

  fetchCategories: (workspaceId: string) => Promise<void>;
  fetchQuestionsForCategory: (categoryId: string) => Promise<void>;
  getActiveCategories: () => WorkspaceItemCategory[];
  toggleActive: (id: string, isActive: boolean) => Promise<void>;
  addCategory: (
    workspaceId: string,
    label: string,
    color: string | null,
    description: string | null,
    altitudeLabels?: Record<1 | 2 | 3 | 4 | 5, string | null>
  ) => Promise<WorkspaceItemCategory | null>;
  editCategory: (
    id: string,
    updates: Partial<Pick<WorkspaceItemCategory, 'label' | 'color' | 'description' | 'altitudeLabels'>>
  ) => Promise<void>;
  removeCategory: (id: string) => Promise<void>;
  saveQuestion: (
    categoryId: string,
    question: Omit<WorkspaceCategoryQuestion, 'id' | 'categoryId'>
  ) => Promise<void>;
}

export const useItemCategoriesStore = create<ItemCategoriesState>((set, get) => ({
  categories: [],
  questions: {},
  loading: false,
  error: null,
  loadedWorkspaceId: null,

  fetchCategories: async (workspaceId) => {
    if (get().loadedWorkspaceId === workspaceId && get().categories.length > 0) return;
    set({ loading: true, error: null });
    const { data, error } = await listItemCategories(workspaceId);
    set({ categories: data, loading: false, error, loadedWorkspaceId: workspaceId });
  },

  fetchQuestionsForCategory: async (categoryId) => {
    if (get().questions[categoryId]) return;
    const { data } = await listCategoryQuestions(categoryId);
    set((state) => ({ questions: { ...state.questions, [categoryId]: data } }));
  },

  getActiveCategories: () => get().categories.filter((c) => c.isActive),

  toggleActive: async (id, isActive) => {
    set((state) => ({
      categories: state.categories.map((c) => (c.id === id ? { ...c, isActive } : c)),
    }));
    const { error } = await setCategoryActive(id, isActive);
    if (error) {
      set((state) => ({
        categories: state.categories.map((c) => (c.id === id ? { ...c, isActive: !isActive } : c)),
        error,
      }));
    }
  },

  addCategory: async (workspaceId, label, color, description, altitudeLabels) => {
    const slug = label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    const maxOrder = Math.max(0, ...get().categories.map((c) => c.sortOrder));
    const { data, error } = await createCategory(
      workspaceId,
      slug,
      label.trim(),
      color,
      description,
      maxOrder + 1,
      altitudeLabels
    );
    if (error) {
      set({ error });
      return null;
    }
    if (data) {
      set((state) => ({ categories: [...state.categories, data] }));
    }
    return data;
  },

  editCategory: async (id, updates) => {
    set((state) => ({
      categories: state.categories.map((c) => (c.id === id ? { ...c, ...updates } : c)),
    }));
    const { error } = await updateCategory(id, updates);
    if (error) set({ error });
  },

  removeCategory: async (id) => {
    const prev = get().categories;
    set((state) => ({ categories: state.categories.filter((c) => c.id !== id) }));
    const { error } = await deleteCategory(id);
    if (error) set({ categories: prev, error });
  },

  saveQuestion: async (categoryId, question) => {
    const { error } = await upsertCategoryQuestion(categoryId, question);
    if (error) {
      set({ error });
      return;
    }
    // Refresh questions for this category
    const { data } = await listCategoryQuestions(categoryId);
    set((state) => ({ questions: { ...state.questions, [categoryId]: data } }));
  },
}));
